import { useState } from 'react'
import { Icon } from '@iconify/react'
import {
  Button,
  CompanyLogo,
  PopOver,
  Spin,
  useShowMessage,
  useSEOCountriesJSON,
  useProfileIntegrationsList,
  useContentRefreshDomains,
} from '@pepper/ui'
import { GaGscType } from '@pepper/utils/src/types/domain'

import { useGaGscProperties } from 'utils/hooks/useGaGscProperties'
import { getAutoMatchProperties } from 'utils/functions/domain'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { useGscValidation } from 'utils/api/useGscValidation'
import { useGaValidation } from 'utils/api/useGaValidation'
import { addDomains } from 'utils/api/domain'
import ConnectGoogleAccount from './ConnectGoogleAccount'
import GoogleConnectionStatus from './GoogleConnectionStatus'
import GoogleAccountProperties from './GoogleAccountProperties'
import SkipAccountIntegration from './SkipAccountIntegration'

import s from './IntegrateDomain.module.css'

type Props = {
  formValues: {
    mainDomain: string
    country: SEOCountries
    brandWords: string[]
    competitorUrl?: string[]
  }
  onPrevious: () => void
  removeSelfModal: () => void
}

export type ModalUiTypes =
  | 'skip integration'
  | 'remove integration'
  | 'no domain integration'
  | 'limited integration'
  | ''

export const IntegrateDomain = ({
  formValues,
  onPrevious,
  removeSelfModal,
}: Props): JSX.Element => {
  const [isAddingDomain, setIsAddingDomain] = useState(false)
  const companyId = useSelectedCompanyId()
  const showMessage = useShowMessage()
  const { data: countries } = useSEOCountriesJSON()
  const { country, mainDomain, brandWords, competitorUrl } = formValues
  const selectedCountry = countries?.[country]
  const { data: domains } = useContentRefreshDomains(companyId)

  const [selectedGaProperty, setSelectedGaProperty] = useState<string>()
  const [selectedGscProperty, setSelectedGscProperty] = useState<string>()

  const { gaOptions, gscOptions, isLoading, gaError, gscError } =
    useGaGscProperties(companyId)

  const { data: gscValidation, isLoading: isValidatingGsc } = useGscValidation({
    companyId,
    uri: selectedGscProperty,
    country: countries?.[country].value,
    enabled: !!selectedGscProperty?.length,
  })

  const { data: gaValidation, isLoading: isValidatingGa } = useGaValidation({
    companyId,
    uriId: selectedGaProperty,
    country: countries?.[country].value,
    enabled: !!selectedGaProperty?.length,
  })

  const { data: integrationsData } = useProfileIntegrationsList({
    companyId,
    enabled: !!companyId,
  })

  const googleIntegrationData = integrationsData?.records.find(
    record => record.integration.name === 'Google'
  )

  const isGoogleConnected = !!googleIntegrationData?.accounts?.length

  const { gaExactMatch, gscExactMatch, hasGaAccess, hasGscAccess } =
    getAutoMatchProperties({
      domain: mainDomain,
      gaProperties: gaOptions,
      gscProperties: gscOptions,
    })

  const hasBothAutoMatched = !!gaExactMatch?.length && !!gscExactMatch?.length

  const handleSubmit = async () => {
    const filteredCompetitorUrls = competitorUrl?.filter(url => url.length > 0)
    setIsAddingDomain(true)
    const response = await addDomains({
      companyId,
      url: mainDomain,
      competitors: filteredCompetitorUrls?.length
        ? filteredCompetitorUrls
        : undefined,
      country_code: country,
      brand_keywords: brandWords,
      gsc_domain_property: selectedGscProperty?.length
        ? selectedGscProperty
        : undefined,
      ga_property: selectedGaProperty?.length ? selectedGaProperty : undefined,
    })
    if (response.status === 'error') {
      showMessage({ type: 'error', message: response.error })
    } else {
      showMessage({ type: 'success', message: 'Domain added successfully' })
    }
    setIsAddingDomain(false)
    removeSelfModal()
  }

  const handleChange = (name: GaGscType, value?: string) => {
    if (name === 'ga_property') {
      setSelectedGaProperty(value)
    } else {
      setSelectedGscProperty(value)
    }
  }

  const gaCount = gaOptions?.length
  const gscCount = gscOptions?.length

  const connectionDescription = () => {
    if (!!gaCount && !!gscCount) {
      if (!!selectedGaProperty && !!selectedGscProperty) {
        return 'We could not automatically match the account properties to your domain.'
      } else if (!hasBothAutoMatched || !!gaCount || !!gscCount) {
        return 'We could not automatically match the account properties to your domain. Please select the relevant properties in the dropdowns below.'
      }
      return 'We have automatically detected the relevant properties on your Google Search Console and Google Analytics.'
    } else if (!gaCount && !gscCount) {
      return 'Either you don’t have access or properties available for both Google Analytics & Search Console.'
    } else {
      return 'Try connecting another Google account that has access to your domain to unlock all features:'
    }
  }

  const connectionTitle = () => {
    if (hasBothAutoMatched) {
      return 'Your Google account is connected!'
    } else if (!!selectedGaProperty && !!selectedGscProperty) {
      return 'Your Google account is connected.'
    } else if (!!gaCount || !!gscCount) {
      return 'Your Google account is connected. Please select properties...'
    } else {
      return 'Your Google account is connected, but you do not have access...'
    }
  }

  if (gscError) {
    showMessage({ type: 'error', message: gscError })
  }
  if (gaError) {
    showMessage({ type: 'error', message: gaError })
  }

  return (
    <Spin wrapperClassName={s.spinWrapper} spinning={isAddingDomain}>
      <div className={s.wrapper}>
        <div className={s.connectGoogleWrapper}>
          <div className={s.header}>
            <div className={s.headerText}>
              2. Connect your Google account for smart and accurate insights.
            </div>
          </div>
          {isGoogleConnected && (
            <GoogleConnectionStatus
              hasAutoMatched={hasBothAutoMatched}
              hasManuallyMatched={!!selectedGaProperty && !!selectedGscProperty}
              isGscSelected={!!selectedGscProperty}
              title={connectionTitle()}
              description={connectionDescription()}
            />
          )}
          <div className={s.domain}>
            <div className={s.label}>
              <CompanyLogo className={s.logo} companyName={mainDomain} />
              <div className={s.url}>{mainDomain}</div>
              <div className={s.country}>
                <img
                  src={selectedCountry?.flag}
                  alt={selectedCountry?.label}
                  width={20}
                  height={16}
                />
                {selectedCountry?.label}
              </div>
            </div>
            {isGoogleConnected ? (
              <Spin spinning={isLoading}>
                <GoogleAccountProperties
                  userEmail={googleIntegrationData?.accounts?.[0].url ?? ''}
                  googleIconUrl={googleIntegrationData?.integration.icon}
                  googleAccountProperties={{
                    gaOptions,
                    gscOptions,
                  }}
                  domains={domains}
                  accountId={googleIntegrationData?.accounts?.[0]?.id}
                  companyId={companyId}
                  onChange={(name: GaGscType, value?: string) => {
                    handleChange(name, value)
                  }}
                  isGaValid={gaValidation?.data || isValidatingGa}
                  isGscValid={gscValidation?.data || isValidatingGsc}
                  gaMatch={gaExactMatch}
                  gscMatch={gscExactMatch}
                />
              </Spin>
            ) : (
              <ConnectGoogleAccount companyId={companyId} />
            )}
          </div>
        </div>
        <div className={s.footer}>
          <button className={s.btn} onClick={onPrevious}>
            <Icon icon="ic:round-chevron-left" width={20} />
            Previous
          </button>

          {isGoogleConnected &&
            (!!selectedGaProperty && !!selectedGscProperty ? (
              <Button type="primary" onClick={handleSubmit}>
                Finish
              </Button>
            ) : (
              <PopOver
                arrowPointAtCenter={true}
                placement="topLeft"
                trigger="click"
                content={
                  <SkipAccountIntegration
                    onSkipConnection={handleSubmit}
                    companyId={companyId}
                    skipped={!selectedGaProperty ? 'gaProperty' : 'gscProperty'}
                  />
                }
              >
                <Button type="default">Skip & Finish</Button>
              </PopOver>
            ))}

          {!isGoogleConnected && (
            <PopOver
              arrowPointAtCenter={true}
              placement="topLeft"
              trigger="click"
              content={
                <SkipAccountIntegration
                  onSkipConnection={handleSubmit}
                  companyId={companyId}
                  skipped="googleAccount"
                />
              }
            >
              <Button type="default">Finish without connecting</Button>
            </PopOver>
          )}
        </div>
      </div>
    </Spin>
  )
}
